/**
 * src/App.tsx — Application shell.
 */

import { useState, useMemo, useEffect, lazy, Suspense } from "react";
import { AuthProvider, useAuthContext } from "./auth/AuthContext";
import { sharePointConfig } from "./auth/msalConfig";
import { getPermissions } from "./auth/roles";

import LoginPage    from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ProfilePage   from "./pages/ProfilePage";
import SettingsPage  from "./pages/SettingsPage";
import ReportsPage, { EmptyPageIcon } from "./pages/ReportsPage";
import DocumentsPage from "./pages/DocumentsPage";
import SuiviPage     from "./pages/SuiviPage";
import TopNav, { type NavItem } from "./components/TopNav";
import LoadingScreen from "./components/LoadingScreen";
import MaintenancePage from "./pages/MaintenancePage";
import { APP_CONFIG } from "./config/app.config";
import { useMinDuration } from "./hooks/useMinDuration";

// Lazy-chargés : tirent ReportA4.tsx (2 500+ lignes + Highcharts + Quill)
// Le bundle initial ne les parse pas ; ils sont chargés à la 1ère ouverture.
const ReportCreator = lazy(() => import("./pages/ReportCreator"));
const ReportViewer  = lazy(() => import("./pages/ReportViewer"));

import type { ReportInstance } from "./report-engine/types";

const ALL_NAV_ITEMS: (NavItem & { permKey?: string; flagged?: boolean })[] = [
  { id:"dashboard",  label:"Accueil",    icon:"🏠" },
  { id:"reports",    label:"Rapports",   icon:<EmptyPageIcon size={24}/>, permKey:"canAccessReports" },
  { id:"analytics",  label:"Suivi",      icon:"📊",  permKey:"canAccessAnalytics" },
  { id:"data",       label:"Data",       icon:"🗄️",  permKey:"canAccessData" },
  // Documents : masqué si VITE_SHAREPOINT_ENABLED=false
  { id:"documents",  label:"Documents",  icon:"📁",  permKey:"canAccessDocuments", flagged: !APP_CONFIG.sharepointEnabled },
  { id:"settings",   label:"Paramètres", icon:"⚙️",  permKey:"canAccessSettings" },
];

function PlaceholderPage({ id }: { id: string }) {
  const labels: Record<string,string> = { analytics:"Suivi", data:"Data" };
  if (id === "403") return (
    <div className="screen">
      <div className="screen-inner">
        <div className="card p-32" style={{ textAlign:"center", color:"var(--text-faint)", marginTop:60 }}>
          <div style={{ fontSize:36, marginBottom:12 }}>🔒</div>
          <p style={{ fontSize:14, fontWeight:600 }}>Accès restreint</p>
          <p style={{ fontSize:12, marginTop:8 }}>Vous n'avez pas les droits nécessaires pour accéder à cette section.</p>
        </div>
      </div>
    </div>
  );
  return (
    <div className="screen">
      <div className="screen-inner">
        <h1 className="page-title">{labels[id] ?? id}</h1>
        <div className="card p-32" style={{ textAlign:"center", color:"var(--text-faint)", marginTop:28 }}>
          <p style={{ fontSize:14 }}>Cette section sera disponible prochainement.</p>
        </div>
      </div>
    </div>
  );
}


type View =
  | { type:"page"; id:string }
  | { type:"creator" }
  | { type:"viewer"; instance:ReportInstance };

function AppShell() {
  const { isAuthenticated, isLoading, getMicrosoftToken, user, role, sessionExpiresAt, sessionInvalid, reconnectingSilently, reauthenticate } = useAuthContext();
  const [reconnecting, setReconnecting] = useState(false);
  const [extendingSession, setExtendingSession] = useState(false);
  const reconnectingSilentlyDelayed = useMinDuration(reconnectingSilently);
  const extendingSessionDelayed = useMinDuration(extendingSession);

  // Mémoïsation : ne recalcule que si `role` change
  const perms     = useMemo(() => getPermissions(role), [role]);
  const NAV_ITEMS = useMemo<NavItem[]>(() => ALL_NAV_ITEMS.filter(item => {
    if (item.flagged) return false;                                      // feature flag désactivé
    if (!item.permKey) return true;
    return perms[item.permKey as keyof typeof perms] !== false;          // filtre par permission
  }), [perms]);

  const [view, setView] = useState<View>({ type:"page", id:"dashboard" });
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  const [maintenanceActive, setMaintenanceActive] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState<string | undefined>(undefined);

  // Efface le tab forcé dès que l'utilisateur quitte Paramètres
  useEffect(() => {
    if (view.type !== "page" || view.id !== "settings") setSettingsInitialTab(undefined);
  }, [view]);

  // SSE maintenance — connecté dès le montage (avant auth) pour couvrir la page de login
  useEffect(() => {
    const es = new EventSource("/api/maintenance/stream");
    es.onmessage = (e) => {
      try { setMaintenanceActive((JSON.parse(e.data) as { active: boolean }).active); } catch {}
    };
    return () => es.close();
  }, []);

  // Alerte 15 min avant l'expiration de session
  useEffect(() => {
    if (!isAuthenticated || !sessionExpiresAt) { setShowExpiryWarning(false); return; }
    const warnAt = sessionExpiresAt - 15 * 60 * 1000;
    const delay  = warnAt - Date.now();
    if (delay <= 0) { setShowExpiryWarning(true); return; }
    const t = setTimeout(() => setShowExpiryWarning(true), delay);
    return () => clearTimeout(t);
  }, [isAuthenticated, sessionExpiresAt]);

  // Reconnexion interactive (popup autorisée — toujours déclenchée par un clic) : bandeau "Se
  // reconnecter" (session déjà invalide, cf. sessionInvalid ci-dessous).
  async function handleReconnect() {
    setReconnecting(true);
    try {
      const ok = await reauthenticate();
      if (ok) setShowExpiryWarning(false);
    } finally {
      setReconnecting(false);
    }
  }

  // Même flux que handleReconnect(), mais pour le bandeau "Prolonger la session" (session encore
  // valide, juste bientôt expirée) — LoadingScreen dédié avec un message distinct ("Prolongation…"
  // plutôt que "Reconnexion…") pour ne pas laisser croire que la session était déjà coupée.
  async function handleExtendSession() {
    setExtendingSession(true);
    try {
      const ok = await reauthenticate();
      if (ok) setShowExpiryWarning(false);
    } finally {
      setExtendingSession(false);
    }
  }

  const activeNavId =
    view.type === "page"    ? view.id :
    view.type === "creator" ? "reports" : "reports";

  // Restauration de session au chargement de l'app (mount) — libellé par défaut de LoadingScreen
  // ("Chargement…", cf. Suspense plus bas pour les pages chargées à la demande). "Connexion…" est
  // réservé au flow de login déclenché depuis LoginPage (isAuthenticating, cf. LoginPage.tsx).
  const isLoadingDelayed = useMinDuration(isLoading);
  if (isLoadingDelayed) return <LoadingScreen/>;
  if (!isAuthenticated) return <LoginPage/>;
  if (maintenanceActive && role !== "super-admin") return <MaintenancePage />;

  // spConfig : uniquement si le feature flag est actif ET que l'utilisateur est connecté via Microsoft
  const spConfig = APP_CONFIG.sharepointEnabled && user?.provider === "microsoft"
    ? { ...sharePointConfig, getAccessToken: getMicrosoftToken as () => Promise<string> }
    : undefined;

  const navigate = (id: string) => setView({ type:"page", id });

  function renderContent() {
    if (view.type === "creator") return (
      <ReportCreator
        onCreated={inst => setView({ type:"viewer", instance:inst })}
        onCancel={() => setView({ type:"page", id:"reports" })}
      />
    );
    if (view.type === "viewer") return (
      <ReportViewer
        instance={view.instance}
        onBack={() => setView({ type:"page", id:"reports" })}
        sharePoint={spConfig}
        readOnly={!perms.canEditReport}
      />
    );
    switch (view.id) {
      case "dashboard": return <DashboardPage onNavigate={id => id === "report" ? navigate("reports") : navigate(id)}/>;
      case "reports":   return (
        <ReportsPage
          onOpen={inst => setView({ type:"viewer", instance:inst })}
          onCreate={() => setView({ type:"creator" })}
          readOnly={!perms.canCreateReport}
        />
      );
      case "documents": return perms.canAccessDocuments ? <DocumentsPage/> : <PlaceholderPage id="403"/>;
      case "analytics": return perms.canAccessAnalytics ? <SuiviPage/> : <PlaceholderPage id="403"/>;
      case "data":      return perms.canAccessData ? <PlaceholderPage id="data"/> : <PlaceholderPage id="403"/>;
      case "profile":   return <ProfilePage/>;
      case "settings":  return perms.canAccessSettings
        ? <SettingsPage userRole={role} initialTab={settingsInitialTab} onMaintenanceChange={setMaintenanceActive}/>
        : <PlaceholderPage id="403"/>;
      default:          return <PlaceholderPage id={view.id}/>;
    }
  }

  const showMaintBadge = maintenanceActive && role === "super-admin";

  return (
    <div style={{ height:"100%", display:"flex", flexDirection:"column" }}>
      <TopNav
        items={NAV_ITEMS} activeId={activeNavId} onNavigate={navigate} onProfile={() => navigate("profile")}
        maintenanceBadge={showMaintBadge}
        onMaintenanceBadgeClick={() => { setSettingsInitialTab("system"); setView({ type:"page", id:"settings" }); }}
      />

      {/* Spacer sous la navbar */}
      <div style={{ height:56, flexShrink:0 }}/>

      {/* Zone de contenu scrollable — scrollbar verte démarre ici */}
      <div style={{ flex:1, minHeight:0, overflowY:"auto" }}>
        <Suspense fallback={<LoadingScreen/>}>
          {renderContent()}
        </Suspense>
      </div>

      {/* Rafraîchissement silencieux réactif en cours (token expiré, cf. authFetch.ts::trySilentReauth())
          — la requête d'origine attend ce rafraîchissement avant de repartir ; sans ce retour visuel
          l'app semblerait figée le temps de l'aller-retour ssoSilent. Jamais affiché pour le
          rafraîchissement périodique proactif, qui reste invisible tant qu'il réussit. */}
      {reconnectingSilentlyDelayed && <LoadingScreen message="Reconnexion à votre session…" overlay/>}

      {/* Prolongation de session déclenchée par un clic sur "Prolonger la session" ci-dessous —
          même mécanique que le rafraîchissement réactif, message dédié pour ne pas laisser croire
          que la session était déjà coupée (elle est juste bientôt expirée). */}
      {extendingSessionDelayed && <LoadingScreen message="Prolongation de votre session…" overlay/>}

      {/* Session invalide : un rafraîchissement silencieux a déjà échoué en tâche de fond (cf.
          authFetch.ts::notifySessionInvalid()) — plus aucun appel API n'aboutira sans reconnexion
          interactive. Prioritaire sur le simple avertissement "expire bientôt" ci-dessous. */}
      {sessionInvalid && (
        <div style={{ position:"fixed", bottom:90, right:28, zIndex:1500,
          maxWidth:420, width:"calc(100% - 56px)", flexDirection:"column", gap:10 }}
          className="toast toast-err">
          <span>🔒 Votre session a expiré. Les actions nécessitant le serveur (sauvegarde, chargement de données…) ne fonctionneront plus tant que vous ne vous êtes pas reconnecté.</span>
          <button
            onClick={handleReconnect}
            disabled={reconnecting}
            className="btn btn-danger btn-sm"
            style={{ alignSelf:"flex-end" }}>
            {reconnecting ? "Reconnexion…" : "Se reconnecter"}
          </button>
        </div>
      )}

      {/* Bandeau d'avertissement expiration de session (~15 min avant l'expiration réelle du token, cf. AuthContext.tsx) */}
      {!sessionInvalid && showExpiryWarning && (
        <div style={{ position:"fixed", bottom:90, right:28, zIndex:1500,
          maxWidth:420, width:"calc(100% - 56px)", flexDirection:"column", gap:10 }}
          className="toast toast-err">
          <div style={{ display:"flex", alignItems:"center", width:"100%" }}>
            <span>⏱ Votre session expire dans moins de 15 min.</span>
            <button onClick={() => setShowExpiryWarning(false)}
              style={{ marginLeft:"auto", background:"none", border:"none", cursor:"pointer",
                color:"var(--red)", fontSize:20, lineHeight:1, padding:"0 0 0 16px" }}>
              ×
            </button>
          </div>
          <button
            onClick={handleExtendSession}
            disabled={extendingSession}
            className="btn btn-danger btn-sm"
            style={{ alignSelf:"flex-end" }}>
            {extendingSession ? "Prolongation…" : "Prolonger la session"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function App() {
  return <AuthProvider><AppShell/></AuthProvider>;
}
